import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Layout } from '@/components/layout';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Barcode, CheckCircle2, XCircle, Loader2, Store, ChevronDown,
  ToggleLeft, ToggleRight, AlertCircle, AlertTriangle, Play, SquareCheck, Square,
  BookmarkPlus, Trash2, ChevronRight,
} from 'lucide-react';
import {
  ShopifyTargetStoreSelector,
} from '@/components/shopify-target-store';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShopifyStore { id: string; nickname: string; storeUrl: string }

interface VariantRow {
  variantId: number;
  variantTitle: string;
  sku: string;
  option1: string;
  option2: string;
  option3: string;
  productId: number;
  productTitle: string;
  vendor: string;
  productType: string;
  handle: string;
}

interface ApplyEvent {
  type: 'start' | 'variant' | 'done' | 'error';
  variantId?: number;
  sku?: string;
  success?: boolean;
  error?: string;
  processed?: number;
  total?: number;
  totalUpdated?: number;
  totalErrors?: number;
  message?: string;
}

interface SkuTemplate {
  id: string;
  name: string;
  format: string;
  createdAt: string;
}

// ── Token definitions ─────────────────────────────────────────────────────────

const TOKENS = [
  { token: '{vendor}',       label: 'Vendor',       key: 'vendor' as keyof VariantRow },
  { token: '{product_type}', label: 'Product Type',  key: 'productType' as keyof VariantRow },
  { token: '{option1}',      label: 'Option 1',      key: 'option1' as keyof VariantRow },
  { token: '{option2}',      label: 'Option 2',      key: 'option2' as keyof VariantRow },
  { token: '{option3}',      label: 'Option 3',      key: 'option3' as keyof VariantRow },
  { token: '{handle}',       label: 'Handle',        key: 'handle' as keyof VariantRow },
];

// Resolve a token name (e.g. "vendor", "product_type") to its VariantRow value.
function resolveTokenValue(name: string, row: VariantRow): string {
  const def = TOKENS.find(t => t.token === `{${name}}`);
  if (!def) return '';
  return String(row[def.key] ?? '');
}

function applyFormat(format: string, row: VariantRow, seq?: string): string {
  let result = format;

  // 1. Replace modifier tokens: {key:N} = first N chars, {key:-N} = last N chars.
  //    Must run before simple replacement so {vendor:2} is handled before {vendor}.
  result = result.replace(/\{([a-z][a-z0-9_]*):(-?\d+)\}/g, (_match, name, nStr) => {
    const val = resolveTokenValue(name, row);
    const n = parseInt(nStr, 10);
    if (n > 0) return val.slice(0, n);
    if (n < 0) return val.slice(n);
    return val;
  });

  // 2. Replace plain tokens: {vendor}, {product_type}, etc.
  for (const { token, key } of TOKENS) {
    result = result.replaceAll(token, String(row[key] ?? ''));
  }

  // 3. Sequence token.
  result = result.replaceAll('{seq}', seq ?? '');

  return result.toUpperCase().replace(/\s+/g, '-');
}

// ── Store selector hook ───────────────────────────────────────────────────────

function useStores(token: string | null) {
  return useQuery<ShopifyStore[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load stores');
      return r.json();
    },
    enabled: !!token,
  });
}

// ── Variant data hook ─────────────────────────────────────────────────────────

function useVariants(storeId: string | null, token: string | null) {
  return useQuery<VariantRow[]>({
    queryKey: ['sku-creator-variants', storeId],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/sku-creator/products?storeId=${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to fetch variants');
      }
      const data = await r.json();
      if (!Array.isArray(data.variants)) throw new Error('Invalid variants response');
      return data.variants as VariantRow[];
    },
    enabled: !!storeId && !!token,
    staleTime: 60_000,
  });
}

// ── SKU Templates hooks ───────────────────────────────────────────────────────

function useSkuTemplates(token: string | null) {
  return useQuery<SkuTemplate[]>({
    queryKey: ['sku-templates'],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/sku-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load templates');
      const data = await r.json();
      if (!Array.isArray(data.templates)) throw new Error('Invalid templates response');
      return data.templates as SkuTemplate[];
    },
    enabled: !!token,
  });
}

// ── Token chip with modifier picker ──────────────────────────────────────────

const FIRST_N = [1, 2, 3, 4, 5];
const LAST_N  = [1, 2, 3, 4, 5];

function TokenChip({ tokenDef, onInsert }: {
  tokenDef: typeof TOKENS[number];
  onInsert: (tok: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const name = tokenDef.token.slice(1, -1); // '{vendor}' → 'vendor'

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      // Close if click is outside both the chip wrapper and the portal dropdown
      const portalEl = document.getElementById(`token-chip-portal-${name}`);
      if (
        wrapRef.current && !wrapRef.current.contains(target) &&
        (!portalEl || !portalEl.contains(target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, name]);

  const dropdown = open && pos ? createPortal(
    <div
      id={`token-chip-portal-${name}`}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden w-64"
    >
      <div className="px-3 py-2 text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider border-b border-border/50">
        {tokenDef.label} — insert as
      </div>
      {/* Full value */}
      <button
        type="button"
        onClick={() => { onInsert(tokenDef.token); setOpen(false); }}
        className="w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors flex items-center justify-between group"
      >
        <span className="font-mono text-primary">{tokenDef.token}</span>
        <span className="text-muted-foreground/70 group-hover:text-foreground/50 text-[10px]">full value</span>
      </button>
      <div className="border-t border-border/50" />
      {/* First N chars */}
      <div className="px-3 pt-1.5 pb-0.5 text-[10px] text-muted-foreground/60 uppercase tracking-wider">First N chars</div>
      <div className="flex flex-wrap gap-1 px-3 pb-2">
        {FIRST_N.map(n => {
          const tok = `{${name}:${n}}`;
          return (
            <button
              key={tok}
              type="button"
              onClick={() => { onInsert(tok); setOpen(false); }}
              className="px-2 py-1 rounded-lg bg-muted/50 hover:bg-primary/20 border border-border hover:border-primary/30 text-xs font-mono text-foreground hover:text-primary transition-colors"
            >
              {tok}
            </button>
          );
        })}
      </div>
      <div className="border-t border-border/50" />
      {/* Last N chars */}
      <div className="px-3 pt-1.5 pb-0.5 text-[10px] text-muted-foreground/60 uppercase tracking-wider">Last N chars</div>
      <div className="flex flex-wrap gap-1 px-3 pb-3">
        {LAST_N.map(n => {
          const tok = `{${name}:-${n}}`;
          return (
            <button
              key={tok}
              type="button"
              onClick={() => { onInsert(tok); setOpen(false); }}
              className="px-2 py-1 rounded-lg bg-muted/50 hover:bg-primary/20 border border-border hover:border-primary/30 text-xs font-mono text-foreground hover:text-primary transition-colors"
            >
              {tok}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-stretch rounded-lg bg-primary/10 border border-primary/20">
        {/* Main chip — inserts full token */}
        <button
          type="button"
          onClick={() => onInsert(tokenDef.token)}
          className="px-3 py-1.5 text-primary text-xs font-mono font-medium hover:bg-primary/20 transition-colors"
        >
          {tokenDef.token}
          <span className="ml-1 text-primary/60 font-sans font-normal">— {tokenDef.label}</span>
        </button>
        {/* Modifier trigger */}
        <button
          ref={triggerRef}
          type="button"
          onClick={openDropdown}
          title="Insert first / last N characters"
          className="px-1.5 border-l border-primary/20 text-primary/40 hover:text-primary hover:bg-primary/20 transition-colors text-[10px] select-none"
        >
          ▾
        </button>
      </div>
      {dropdown}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
      <div
        className="h-2 rounded-full bg-primary transition-all duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Save Template Modal ───────────────────────────────────────────────────────

function SaveTemplateModal({
  format,
  token,
  onClose,
}: {
  format: string;
  token: string | null;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${BASE}/api/sku-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), format }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to save template');
      }
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sku-templates'] });
      toast({ title: 'Template saved', description: `"${name.trim()}" is now available in your templates.` });
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="glass-panel rounded-2xl p-6 w-full max-w-sm space-y-4 shadow-xl">
        <h3 className="text-sm font-semibold">Save as Template</h3>
        <p className="text-xs text-muted-foreground">
          Format: <span className="font-mono text-primary">{format || '—'}</span>
        </p>
        <input
          autoFocus
          type="text"
          placeholder="Template name (e.g. Wheels, Apparel)"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && name.trim()) save.mutate(); if (e.key === 'Escape') onClose(); }}
          className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 placeholder:text-muted-foreground/50"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs rounded-xl bg-muted/50 hover:bg-muted border border-border transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="flex items-center gap-2 px-4 py-2 text-xs rounded-xl bg-primary text-black font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {save.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function SkuCreator() {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [storeId, setStoreId] = useState<string | null>(null);
  const [format, setFormat] = useState('{vendor}-{option1}');
  const [skipExisting, setSkipExisting] = useState(true);
  const [missingOnly, setMissingOnly] = useState(true);
  const [seqEnabled, setSeqEnabled] = useState(false);
  const [seqPadding, setSeqPadding] = useState<2 | 3>(3);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showSaveModal, setShowSaveModal] = useState(false);

  // SSE state
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState<{ processed: number; total: number; totalUpdated: number; totalErrors: number } | null>(null);
  const [results, setResults] = useState<Map<number, { success: boolean; error?: string }>>(new Map());
  const [statusMsg, setStatusMsg] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const { data: stores = [], isLoading: storesLoading } = useStores(token);
  const { data: allVariants = [], isLoading: variantsLoading, error: variantsError, refetch: refetchVariants } = useVariants(storeId, token);
  const { data: templates = [] } = useSkuTemplates(token);

  // Delete template mutation
  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${BASE}/api/sku-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to delete template');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sku-templates'] });
      toast({ title: 'Template deleted' });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // Compute generated SKUs (with optional per-product sequence)
  const variantsWithSku = useMemo(() => {
    const productCounters = new Map<number, number>();
    return allVariants.map(v => {
      let seq: string | undefined;
      if (seqEnabled) {
        const n = (productCounters.get(v.productId) ?? 0) + 1;
        productCounters.set(v.productId, n);
        seq = String(n).padStart(seqPadding, '0');
      }
      return { ...v, generatedSku: applyFormat(format, v, seq) };
    });
  }, [allVariants, format, seqEnabled, seqPadding]);

  // Filtered view
  const displayRows = useMemo(() => {
    if (!missingOnly) return variantsWithSku;
    return variantsWithSku.filter(v => !v.sku);
  }, [variantsWithSku, missingOnly]);

  // Rows eligible for selection (based on skipExisting)
  const eligibleRows = useMemo(() =>
    displayRows.filter(v => !skipExisting || !v.sku),
    [displayRows, skipExisting]
  );

  // Toggle selection
  const toggleRow = useCallback((id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllMissing = useCallback(() => {
    setSelected(new Set(eligibleRows.map(v => v.variantId)));
  }, [eligibleRows]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const allEligibleSelected = eligibleRows.length > 0 && eligibleRows.every(v => selected.has(v.variantId));

  const toggleAllEligible = useCallback(() => {
    if (allEligibleSelected) clearSelection();
    else selectAllMissing();
  }, [allEligibleSelected, clearSelection, selectAllMissing]);

  // Insert token at cursor position in format input
  const formatInputRef = useRef<HTMLInputElement>(null);
  const insertToken = useCallback((token: string) => {
    const input = formatInputRef.current;
    if (!input) {
      setFormat(prev => prev + token);
      return;
    }
    const start = input.selectionStart ?? format.length;
    const end = input.selectionEnd ?? format.length;
    const next = format.slice(0, start) + token + format.slice(end);
    setFormat(next);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + token.length;
      input.setSelectionRange(pos, pos);
    });
  }, [format]);

  // Duplicate SKU detection among selected+eligible rows
  const duplicateSkus = useMemo<Set<string>>(() => {
    const skuCounts = new Map<string, number>();
    for (const id of selected) {
      const v = variantsWithSku.find(r => r.variantId === id);
      if (!v || (skipExisting && v.sku)) continue;
      skuCounts.set(v.generatedSku, (skuCounts.get(v.generatedSku) ?? 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [sku, count] of skuCounts) {
      if (count > 1) dupes.add(sku);
    }
    return dupes;
  }, [selected, variantsWithSku, skipExisting]);

  // Apply SKUs via SSE
  const applySkus = useCallback(async () => {
    if (!storeId || selected.size === 0) return;

    if (duplicateSkus.size > 0) {
      toast({ title: 'Duplicate SKUs detected', description: 'Resolve all duplicate generated SKUs before applying.', variant: 'destructive' });
      return;
    }

    const assignments = [...selected]
      .map(id => variantsWithSku.find(v => v.variantId === id))
      .filter(Boolean)
      .filter(v => !skipExisting || !v!.sku)
      .map(v => ({ variantId: v!.variantId, sku: v!.generatedSku }));

    if (assignments.length === 0) {
      toast({ title: 'Nothing to apply', description: 'All selected variants already have SKUs (skip existing is on).' });
      return;
    }

    setRunning(true);
    setDone(false);
    setProgress(null);
    setResults(new Map());
    setStatusMsg('Starting…');

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const resp = await fetch(`${BASE}/api/sku-creator/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ storeId, assignments }),
        signal: ctrl.signal,
      });

      if (!resp.ok || !resp.body) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error ?? 'Apply failed');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedDone = false;

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          if (!part.trim() || part.startsWith(':')) continue;
          const eventMatch = part.match(/^event:\s*(.+)$/m);
          const dataMatch  = part.match(/^data:\s*(.+)$/m);
          if (!dataMatch) continue;

          const evtType = eventMatch?.[1]?.trim() ?? 'message';
          let payload: any;
          try { payload = JSON.parse(dataMatch[1]!); } catch { continue; }

          if (evtType === 'start') {
            setStatusMsg(payload.message ?? 'Applying…');
          } else if (evtType === 'variant') {
            setResults(prev => new Map(prev).set(payload.variantId, { success: payload.success, error: payload.error }));
            setProgress({ processed: payload.processed, total: payload.total, totalUpdated: payload.totalUpdated, totalErrors: payload.totalErrors });
          } else if (evtType === 'done') {
            receivedDone = true;
            setStatusMsg(payload.message ?? 'Done.');
            setProgress(p => p ? { ...p, processed: payload.total } : p);
            setDone(true);
            setRunning(false);
            refetchVariants();
          } else if (evtType === 'error') {
            setStatusMsg(`Error: ${payload.message}`);
            setRunning(false);
          }
        }
      }
      if (!receivedDone && !ctrl.signal.aborted) {
        setStatusMsg('Connection closed before completion. Refresh variants to confirm which SKU updates succeeded.');
        setRunning(false);
        toast({
          title: 'SKU update status incomplete',
          description: 'The update stream ended before completion. Refresh variants before retrying.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        toast({ title: 'Error', description: err.message ?? 'Apply failed', variant: 'destructive' });
      }
      setRunning(false);
    }
  }, [storeId, selected, variantsWithSku, skipExisting, token, toast, refetchVariants, duplicateSkus]);

  const missingCount = allVariants.filter(v => !v.sku).length;
  const selectedEligible = [...selected].filter(id => {
    const v = variantsWithSku.find(r => r.variantId === id);
    return v && (!skipExisting || !v.sku);
  }).length;

  // Rows in the display table that are selected+eligible AND have a duplicate generated SKU
  const duplicateVariantIds = useMemo<Set<number>>(() => {
    const ids = new Set<number>();
    for (const id of selected) {
      const v = variantsWithSku.find(r => r.variantId === id);
      if (!v || (skipExisting && v.sku)) continue;
      if (duplicateSkus.has(v.generatedSku)) ids.add(id);
    }
    return ids;
  }, [selected, variantsWithSku, skipExisting, duplicateSkus]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Barcode className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold">Bulk SKU Creator</h1>
              <p className="text-sm text-muted-foreground">Generate and push SKUs to Shopify using a token-based format</p>
            </div>
          </div>
        </div>

        {/* Store selector */}
        <div className="glass-panel rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Store className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold">Store</h2>
          </div>
          {storesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading stores…
            </div>
          ) : stores.length === 0 ? (
            <p className="text-sm text-muted-foreground">No stores connected. Add a store first.</p>
          ) : (
            <ShopifyTargetStoreSelector
              stores={stores}
              value={storeId ?? ''}
              onChange={v => { setStoreId(v); setSelected(new Set()); setResults(new Map()); setDone(false); }}
              label="Target Shopify store"
              className="max-w-sm"
            />
          )}
        </div>

        {storeId && (
          <>
            {/* Format builder */}
            <div className="glass-panel rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold">SKU Format</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Click a token to insert it, or type freely. Output is uppercased and spaces become dashes.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSaveModal(true)}
                  disabled={!format.trim()}
                  title="Save current format as a template"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xl bg-muted/50 hover:bg-muted border border-border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <BookmarkPlus className="w-3.5 h-3.5" />
                  Save as template
                </button>
              </div>

              {/* Saved templates */}
              {templates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Saved templates</p>
                  <div className="flex flex-wrap gap-2">
                    {templates.map(t => (
                      <div
                        key={t.id}
                        className="group flex items-center gap-1 rounded-xl border border-border bg-muted/50 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => setFormat(t.format)}
                          title={`Load: ${t.format}`}
                          className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 text-xs text-foreground hover:text-foreground transition-colors"
                        >
                          <ChevronRight className="w-3 h-3 text-primary shrink-0" />
                          {t.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTemplate.mutate(t.id)}
                          title="Delete template"
                          className="pr-2 pl-1 py-1.5 text-muted-foreground/50 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Token chips */}
              <div className="flex flex-wrap gap-2">
                {TOKENS.map(tokenDef => (
                  <TokenChip key={tokenDef.token} tokenDef={tokenDef} onInsert={insertToken} />
                ))}
                <button
                  type="button"
                  onClick={() => insertToken('{seq}')}
                  title={seqEnabled ? `Inserts padded sequence number (e.g. ${String(1).padStart(seqPadding, '0')}, ${String(2).padStart(seqPadding, '0')}, …)` : 'Enable "Sequence within product" below to activate this token'}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono font-medium transition-colors ${
                    seqEnabled
                      ? 'bg-primary/10 border-primary/20 text-primary hover:bg-primary/20'
                      : 'bg-muted/50 border-border text-muted-foreground/70 hover:bg-muted'
                  }`}
                >
                  {'{seq}'}
                  <span className={`ml-1 font-sans ${seqEnabled ? 'text-primary/60' : 'text-muted-foreground/50'}`}>— Sequence</span>
                </button>
              </div>

              {/* Format input */}
              <div className="flex items-center gap-3">
                <input
                  ref={formatInputRef}
                  type="text"
                  value={format}
                  onChange={e => setFormat(e.target.value)}
                  placeholder="{vendor}-{option1}"
                  className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-primary/50 placeholder:text-muted-foreground/50"
                />
                <button
                  type="button"
                  onClick={() => setFormat('')}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
                >
                  Clear
                </button>
              </div>

              {/* Preview of format */}
              {format && (
                <p className="text-xs text-muted-foreground">
                  Example output: <span className="font-mono text-primary">{format.toUpperCase().replace(/\s+/g, '-')}</span> (tokens replaced with actual values per row)
                </p>
              )}
            </div>

            {/* Options */}
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <button type="button" onClick={() => setSkipExisting(v => !v)}>
                    {skipExisting
                      ? <ToggleRight className="w-8 h-8 text-primary" />
                      : <ToggleLeft className="w-8 h-8 text-muted-foreground/70" />
                    }
                  </button>
                  <div>
                    <p className="text-sm font-medium">Skip existing SKUs</p>
                    <p className="text-xs text-muted-foreground">Don't overwrite variants that already have a SKU</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <button type="button" onClick={() => setMissingOnly(v => !v)}>
                    {missingOnly
                      ? <ToggleRight className="w-8 h-8 text-primary" />
                      : <ToggleLeft className="w-8 h-8 text-muted-foreground/70" />
                    }
                  </button>
                  <div>
                    <p className="text-sm font-medium">Show missing SKUs only</p>
                    <p className="text-xs text-muted-foreground">
                      {missingCount} variant{missingCount === 1 ? '' : 's'} missing a SKU
                    </p>
                  </div>
                </label>

                <div className="flex items-center gap-3 select-none">
                  <button type="button" onClick={() => setSeqEnabled(v => !v)}>
                    {seqEnabled
                      ? <ToggleRight className="w-8 h-8 text-primary" />
                      : <ToggleLeft className="w-8 h-8 text-muted-foreground/70" />
                    }
                  </button>
                  <div>
                    <p className="text-sm font-medium">Sequence within product</p>
                    <p className="text-xs text-muted-foreground">
                      Enables <span className="font-mono text-primary/80">{'{seq}'}</span> — resets per product, pads to{' '}
                      {seqEnabled ? (
                        <span className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setSeqPadding(2)}
                            className={`px-1.5 py-0.5 rounded text-xs font-mono transition-colors ${seqPadding === 2 ? 'bg-primary text-black font-semibold' : 'bg-muted hover:bg-muted/50'}`}
                          >
                            2
                          </button>
                          <button
                            type="button"
                            onClick={() => setSeqPadding(3)}
                            className={`px-1.5 py-0.5 rounded text-xs font-mono transition-colors ${seqPadding === 3 ? 'bg-primary text-black font-semibold' : 'bg-muted hover:bg-muted/50'}`}
                          >
                            3
                          </button>
                          {' '}digits
                        </span>
                      ) : (
                        <span>2 or 3 digits</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Variants table */}
            <div className="glass-panel rounded-2xl overflow-hidden">
              {/* Table toolbar */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">
                    {variantsLoading ? 'Loading variants…' : `${displayRows.length.toLocaleString()} variant${displayRows.length === 1 ? '' : 's'}`}
                  </span>
                  {selected.size > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {selected.size} selected
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {eligibleRows.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleAllEligible}
                      className="text-xs px-3 py-1.5 rounded-lg bg-muted/50 hover:bg-muted border border-border transition-colors"
                    >
                      {allEligibleSelected ? 'Deselect all' : `Select all ${eligibleRows.length > 0 ? `(${eligibleRows.length})` : ''}`}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={applySkus}
                    disabled={running || selectedEligible === 0 || duplicateSkus.size > 0}
                    title={duplicateSkus.size > 0 ? 'Resolve duplicate SKUs before applying' : undefined}
                    className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-primary text-black text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    Apply SKUs{selectedEligible > 0 ? ` (${selectedEligible})` : ''}
                  </button>
                </div>
              </div>

              {/* Duplicate SKU warning banner */}
              {duplicateSkus.size > 0 && (
                <div className="px-5 py-3 bg-amber-500/10 border-b border-amber-500/20 flex gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-300 space-y-1">
                    <p className="font-semibold">
                      {duplicateSkus.size} duplicate SKU{duplicateSkus.size === 1 ? '' : 's'} detected — Apply is disabled until resolved.
                    </p>
                    <p className="text-amber-300/70">
                      The following generated value{duplicateSkus.size === 1 ? ' appears' : 's appear'} on more than one selected variant:{' '}
                      <span className="font-mono">{[...duplicateSkus].join(', ')}</span>.
                      Add a token like <span className="font-mono">{'{option2}'}</span> to make each SKU unique.
                    </p>
                  </div>
                </div>
              )}

              {/* Progress banner */}
              {(running || done) && (
                <div className="px-5 py-3 bg-muted/50 border-b border-border/50 space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{statusMsg}</span>
                    {progress && (
                      <span className="font-mono">
                        {progress.processed}/{progress.total} • {progress.totalUpdated} ✓ • {progress.totalErrors} ✗
                      </span>
                    )}
                  </div>
                  {progress && (
                    <ProgressBar value={progress.processed} total={progress.total} />
                  )}
                </div>
              )}

              {/* Error state */}
              {variantsError && (
                <div className="flex items-center gap-2 px-5 py-4 text-sm text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  {(variantsError as Error).message}
                </div>
              )}

              {/* Loading */}
              {variantsLoading && !variantsError && (
                <div className="flex items-center gap-2 px-5 py-8 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Fetching variants from Shopify…
                </div>
              )}

              {/* Empty state */}
              {!variantsLoading && !variantsError && displayRows.length === 0 && (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                  {missingOnly ? 'All variants already have SKUs.' : 'No variants found.'}
                </div>
              )}

              {/* Table */}
              {!variantsLoading && !variantsError && displayRows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/50 text-left">
                        <th className="px-4 py-3 w-10">
                          <button type="button" onClick={toggleAllEligible} className="text-muted-foreground hover:text-foreground transition-colors">
                            {allEligibleSelected
                              ? <SquareCheck className="w-4 h-4 text-primary" />
                              : <Square className="w-4 h-4" />
                            }
                          </button>
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product</th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Variant</th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current SKU</th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Generated SKU</th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((row) => {
                        const isEligible = !skipExisting || !row.sku;
                        const isSelected = selected.has(row.variantId);
                        const result = results.get(row.variantId);
                        const isDuplicate = duplicateVariantIds.has(row.variantId);

                        let rowClass = 'border-b border-border/50 transition-colors ';
                        if (!isEligible) {
                          rowClass += 'opacity-40 ';
                        } else if (isDuplicate) {
                          rowClass += 'cursor-pointer bg-amber-500/10 hover:bg-amber-500/15 ';
                        } else {
                          rowClass += `cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''} `;
                        }

                        return (
                          <tr
                            key={row.variantId}
                            onClick={() => isEligible && toggleRow(row.variantId)}
                            className={rowClass}
                          >
                            <td className="px-4 py-3">
                              {isEligible ? (
                                isSelected
                                  ? <SquareCheck className={`w-4 h-4 ${isDuplicate ? 'text-amber-400' : 'text-primary'}`} />
                                  : <Square className="w-4 h-4 text-muted-foreground/70" />
                              ) : (
                                <Square className="w-4 h-4 text-muted-foreground/40" />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <p className="font-medium text-foreground truncate max-w-xs">{row.productTitle}</p>
                              <p className="text-xs text-muted-foreground truncate">{row.vendor}{row.productType ? ` · ${row.productType}` : ''}</p>
                            </td>
                            <td className="px-4 py-3 text-foreground">{row.variantTitle}</td>
                            <td className="px-4 py-3">
                              {row.sku
                                ? <span className="font-mono text-muted-foreground">{row.sku}</span>
                                : <span className="text-muted-foreground/60 italic">—</span>
                              }
                            </td>
                            <td className="px-4 py-3">
                              {isDuplicate ? (
                                <span className="inline-flex items-center gap-1.5 font-mono text-amber-400">
                                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                  {row.generatedSku}
                                </span>
                              ) : (
                                <span className={`font-mono ${isEligible ? 'text-primary' : 'text-muted-foreground/70'}`}>
                                  {row.generatedSku || <span className="italic text-muted-foreground/50">—</span>}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {result ? (
                                result.success
                                  ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                                  : <XCircle className="w-4 h-4 text-red-400" title={result.error} />
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Save template modal */}
      {showSaveModal && (
        <SaveTemplateModal
          format={format}
          token={token}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </Layout>
  );
}
